import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mockArticles, mockVideos, categories } from '../mock';
import { apiService } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Newspaper, 
  Code, 
  Brain, 
  FileText, 
  Wrench, 
  GraduationCap,
  Grid3x3,
  Search,
  Youtube,
  ExternalLink,
  Mail,
  Sparkles,
  Clock,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Calendar
} from 'lucide-react';
  import { Coffee } from 'lucide-react';
import { User } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import PushButton from '../components/PushButton';
import { SignedIn, SignedOut, SignInButton, UserButton, useUser } from '@clerk/clerk-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const DEFAULT_THUMBNAIL = 'https://customer-assets.emergentagent.com/job_aidevnewshub/artifacts/yk3yvwx0_thumbnail.webp';

const iconMap = {
  'Grid3x3': Grid3x3,
  'Newspaper': Newspaper,
  'Code': Code,
  'Brain': Brain,
  'FileText': FileText,
  'Wrench': Wrench,
  'GraduationCap': GraduationCap
};

// Map category names to Tailwind color classes for badges
const categoryColorMap = {
  'AI News': 'bg-indigo-600',
  'Programming': 'bg-green-600',
  'Machine Learning': 'bg-emerald-600',
  'Research Papers': 'bg-rose-600',
  'Tools': 'bg-yellow-600',
  'Tutorials': 'bg-sky-600',
  // fallback
  'default': 'bg-slate-800/90'
};

const getBadgeClass = (category) => {
  const base = 'absolute top-3 left-3 text-white border-0 px-3 py-1.5';
  const color = categoryColorMap[category] || categoryColorMap['default'];
  return `${base} ${color}`;
};

const Home = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [articlesTotal, setArticlesTotal] = useState(0);
  const [articlesSkip, setArticlesSkip] = useState(0);
  const [email, setEmail] = useState('');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();
  
  // Check if user has active Premium subscription
  // Verificar múltiples formas en que Clerk puede almacenar la suscripción
  const isPremium = 
    // Opción 1: publicMetadata configurado manualmente
    user?.publicMetadata?.subscriptionStatus === 'active' || 
    user?.publicMetadata?.subscription === 'premium' ||
    user?.publicMetadata?.plan === 'premium' ||
    user?.publicMetadata?.isPremium === true ||
    // Opción 2: unsafeMetadata (usado por Clerk Billing)
    user?.unsafeMetadata?.subscriptionStatus === 'active' ||
    user?.unsafeMetadata?.subscription === 'premium' ||
    user?.unsafeMetadata?.plan === 'premium' ||
    // Opción 3: privateMetadata (si se sincroniza al frontend)
    user?.privateMetadata?.subscriptionStatus === 'active';
  
  // Debug: log user data to console (remove in production)
  useEffect(() => {
    if (user) {
      console.log('[Premium Check] Full user object:', user);
      console.log('[Premium Check] publicMetadata:', user.publicMetadata);
      console.log('[Premium Check] unsafeMetadata:', user.unsafeMetadata);
      console.log('[Premium Check] isPremium:', isPremium);
    }
  }, [user, isPremium]);
  const articlesPerPage = 6;
  // Hero carousel API and state
  const [heroApi, setHeroApi] = useState(null);
  const [heroSelected, setHeroSelected] = useState(0);
  const [heroHover, setHeroHover] = useState(false);

  // Refs and state to ensure the category filter sits slightly below the fixed header
  const headerRef = useRef(null);
  const categoryRef = useRef(null);
  const [categoryTop, setCategoryTop] = useState(null);
  const scrollContainerRef = useRef(null);
  const categoryScrollRef = useRef(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  useEffect(() => {
    const updateCategoryTop = () => {
      const headerHeight = headerRef.current ? headerRef.current.offsetHeight : 0;
      // add ~6px separation when both elements become fixed/sticky
      setCategoryTop(headerHeight + 18);
    };

    updateCategoryTop();
    window.addEventListener('resize', updateCategoryTop);
    return () => window.removeEventListener('resize', updateCategoryTop);
  }, []);

  // Update gradient fades visibility depending on scroll position
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const updateFades = () => {
      setShowLeftFade(el.scrollLeft > 6);
      setShowRightFade(el.scrollWidth > el.clientWidth + el.scrollLeft + 6);
    };

    updateFades();
    el.addEventListener('scroll', updateFades);
    window.addEventListener('resize', updateFades);
    return () => {
      el.removeEventListener('scroll', updateFades);
      window.removeEventListener('resize', updateFades);
    };
  }, []);

  // Auto refresh every hour
  useEffect(() => {
    const interval = setInterval(() => {
      handleRefresh();
    }, 3600000); // 1 hour

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    // Trigger backend refresh and poll for updated results so the UI updates
    setIsRefreshing(true);
    const previousTotal = articlesTotal;
    const previousFeatured = JSON.stringify(featured || []);

    apiService.refreshContent().then(() => {
      setLastUpdate(new Date());
      toast({
        title: "Sync started",
        description: "Content is being updated in the background...",
      });

      // Poll the dashboard endpoint until new data appears or timeout
      const maxAttempts = 10; // try for up to ~30s (with 3s interval)
      const intervalMs = 3000;
      let attempts = 0;

      const poll = async () => {
        attempts += 1;
        try {
          const data = await apiService.getDashboardData(selectedCategory, articlesPerPage, 0, 5, 12);
          const newTotal = data.total_articles || 0;
          const newFeatured = JSON.stringify(data.featured || []);
          // If counts changed or featured changed, update UI and stop polling
          if (newTotal !== previousTotal || newFeatured !== previousFeatured) {
            setArticles(data.articles || []);
            setArticlesTotal(newTotal);
            setFeatured(data.featured || []);
            setVideos(data.videos || []);
            toast({ title: 'Content updated', description: 'Latest news loaded.' });
            setIsRefreshing(false);
            return;
          }
        } catch (e) {
          // ignore transient errors and retry
        }

        if (attempts < maxAttempts) {
          setTimeout(poll, intervalMs);
        } else {
          // Final fallback: fetch once more and stop
          try {
            loadInitialData(selectedCategory).then(() => {
              toast({ title: 'Content updated', description: 'Latest news loaded.' });
            });
          } catch (e) {
            // ignore
          } finally {
            setIsRefreshing(false);
          }
        }
      };

      // start polling after a short delay to give the backend time to begin scraping
      setTimeout(poll, 1500);

    }).catch((e) => {
      toast({ title: 'Error', description: 'Could not update content.' });
      setIsRefreshing(false);
    });
  };

  // Get available years and months from articles
  const availableYears = [...new Set(articles.map(a => new Date(a.publishedAt).getFullYear()))].sort((a, b) => b - a);
  const availableMonths = [
    { value: '0', label: 'January' },
    { value: '1', label: 'February' },
    { value: '2', label: 'March' },
    { value: '3', label: 'April' },
    { value: '4', label: 'May' },
    { value: '5', label: 'June' },
    { value: '6', label: 'July' },
    { value: '7', label: 'August' },
    { value: '8', label: 'September' },
    { value: '9', label: 'October' },
    { value: '10', label: 'November' },
    { value: '11', label: 'December' }
  ];

  const filteredArticles = articles.filter(article => {
    const articleDate = new Date(article.publishedAt);
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesYear = selectedYear === 'all' || articleDate.getFullYear().toString() === selectedYear;
    const matchesMonth = selectedMonth === 'all' || articleDate.getMonth().toString() === selectedMonth;
    
    return matchesCategory && matchesSearch && matchesYear && matchesMonth;
  });

  const filteredVideos = videos.filter(video => {
    const matchesCategory = selectedCategory === 'all' || video.category === selectedCategory;
    return matchesCategory;
  });

  const featuredArticles = featured;

  // Generate pagination numbers with ellipsis for compact display
  const generatePaginationNumbers = (currentPage, totalPages) => {
    const numbers = [];
    const showEllipsis = totalPages > 7;
    
    if (!showEllipsis) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        numbers.push(i);
      }
    } else {
      // Always show first page
      numbers.push(1);
      
      if (currentPage <= 4) {
        // Near the beginning: 1 2 3 4 5 ... last
        for (let i = 2; i <= Math.min(5, totalPages - 1); i++) {
          numbers.push(i);
        }
        if (totalPages > 5) {
          numbers.push('...');
          numbers.push(totalPages);
        }
      } else if (currentPage >= totalPages - 3) {
        // Near the end: 1 ... last-4 last-3 last-2 last-1 last
        numbers.push('...');
        for (let i = Math.max(2, totalPages - 4); i <= totalPages; i++) {
          numbers.push(i);
        }
      } else {
        // In the middle: 1 ... current-1 current current+1 ... last
        numbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          numbers.push(i);
        }
        numbers.push('...');
        numbers.push(totalPages);
      }
    }
    
    return numbers;
  };

  // Pagination
  const totalPages = Math.ceil(articlesTotal / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  // Use slice with startIndex so numbered pagination shows the right page.
  // If the client hasn't fetched that page yet (e.g. only first page loaded), we'll trigger a fetch in an effect.
  const paginatedArticles = filteredArticles.slice(startIndex, startIndex + articlesPerPage);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [selectedCategory, searchQuery, selectedYear, selectedMonth]);

  // If user navigates to a page we haven't loaded yet, fetch that page from server
  useEffect(() => {
    const ensurePage = async () => {
      const needed = (currentPage - 1) * articlesPerPage + 1;
      if (articles.length < needed) {
        // fetch the page from server
        try {
          const skip = (currentPage - 1) * articlesPerPage;
          const res = await apiService.getArticles(selectedCategory, articlesPerPage, skip);
          // replace the articles array with items up to this page (so filters/pagination behave)
          // We append only if we don't already have them
          setArticles(prev => {
            const existingUrls = new Set(prev.map(a => a.articleUrl));
            const toAdd = (res.articles || []).filter(a => !existingUrls.has(a.articleUrl));
            return [...prev, ...toAdd];
          });
          setArticlesTotal(res.total || articlesTotal);
        } catch (e) {
          console.error('Failed loading page', e);
        }
      }
    };
    ensurePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Load initial data
  const loadInitialData = async (category = 'all') => {
    try {
      setIsRefreshing(true);
      setArticlesSkip(0);
      
      // Single combined API call instead of 3 separate calls
      const data = await apiService.getDashboardData(category, articlesPerPage, 0, 5, 12);
      
      setArticles(data.articles || []);
      setArticlesTotal(data.total_articles || 0);

      // Use backend-provided featured list when available. If backend
      // returns an empty list (or a list dominated by a single source
      // such as 'OpenAI'), fall back to using the most recent articles
      // so the hero carousel feels fresh.
      let fetchedFeatured = data.featured || [];

      const isMonolithicSource = (arr) => {
        if (!arr || arr.length === 0) return false;
        const first = (arr[0] && arr[0].source) || null;
        return arr.every(a => (a && a.source) === first);
      };

      if (!fetchedFeatured.length || isMonolithicSource(fetchedFeatured)) {
        // prefer articles that have a featured flag in the articles result
        const promoted = (data.articles || []).filter(a => a.featured);
        if (promoted.length > 0) {
          fetchedFeatured = promoted.slice(0, 5);
        } else {
          // fallback to the most recent articles
          fetchedFeatured = (data.articles || []).slice(0, 5);
        }
      }

      setFeatured(fetchedFeatured || []);
      setVideos(data.videos || []);
    } catch (e) {
      console.error(e);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadInitialData(selectedCategory);
  }, []);

  // When category changes, reload
  useEffect(() => {
    loadInitialData(selectedCategory);
  }, [selectedCategory]);

  // Scroll selected category into view (center) for better UX on small screens
  const selectAndScroll = (categoryId) => {
    setSelectedCategory(categoryId);
    try {
      const el = document.getElementById(`cat-btn-${categoryId}`);
      if (el && categoryScrollRef.current) {
        el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    } catch (e) {
      // ignore
    }
  };

  // Autoplay for hero carousel: advance every 8s, pause on hover
  useEffect(() => {
    if (!heroApi) return;

    const onSelect = () => setHeroSelected(heroApi.selectedScrollSnap());
    // sync initial selected
    setHeroSelected(heroApi.selectedScrollSnap());
    heroApi.on('select', onSelect);

    let interval = null;
    const start = () => {
      if (interval) return;
      interval = setInterval(() => {
        if (!heroHover && heroApi && heroApi.canScrollNext()) {
          heroApi.scrollNext();
        } else if (!heroHover && heroApi) {
          // loop back to start
          heroApi.scrollTo(0);
        }
      }, 8000);
    };

    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    start();

    return () => {
      stop();
      try { heroApi.off('select', onSelect); } catch (e) {}
    };
  }, [heroApi, heroHover]);

  // When featured articles change, reset hero to first slide for consistency
  useEffect(() => {
    if (heroApi) {
      try {
        heroApi.reInit();
        heroApi.scrollTo(0);
      } catch (e) {}
      setHeroSelected(0);
    }
  }, [featuredArticles, heroApi]);

  const handleLoadMore = async () => {
    try {
      const nextSkip = articlesSkip + articlesPerPage;
      const res = await apiService.getArticles(selectedCategory, articlesPerPage, nextSkip);
      setArticles(prev => [...prev, ...(res.articles || [])]);
      setArticlesSkip(nextSkip);
      setArticlesTotal(res.total || articlesTotal);
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'Could not load more articles.' });
    }
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email) {
      const subscribers = JSON.parse(localStorage.getItem('newsletter_subscribers') || '[]');
      subscribers.push({ email, subscribedAt: new Date().toISOString() });
      localStorage.setItem('newsletter_subscribers', JSON.stringify(subscribers));
      
      toast({
        title: "Subscription successful!",
        description: "You'll receive the latest AI news by email.",
      });
      setEmail('');
    }
  };

  const getImageUrl = (url) => {
    return url || DEFAULT_THUMBNAIL;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
  <header ref={headerRef} className="bg-white/90 backdrop-blur-lg border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-xl shadow-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                  AI News Hub
                </h1>
                <p className="text-xs text-slate-600">Your daily dose of AI & Tech</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
                <div className="hidden md:flex items-center gap-2 text-xs text-slate-500">
                <Clock className="w-4 h-4" />
                <span>Last update: {lastUpdate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <div className="hidden md:flex items-center">
                <div className="mr-2">
                  <PushButton />
                </div>
                {/* Quick link to Hub */}
                <div className="mr-3">
                  <Link to="/hub" className="px-3 py-2 rounded-md text-sm font-medium bg-white border border-slate-200 hover:bg-slate-50 text-slate-700">
                    Hub
                  </Link>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-64 bg-white border-slate-200 focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Small-screen Hub link (visible on mobile/tablet) */}
                <Link to="/hub" className="md:hidden px-2 py-1 rounded text-sm text-slate-700 hover:bg-slate-100 border border-slate-200">
                  Hub
                </Link>
                <SignedOut>
                  <SignInButton>
                    <button aria-label="Open login" className="p-2 rounded-full hover:bg-slate-100">
                      <User className="w-5 h-5 text-slate-700" />
                    </button>
                  </SignInButton>
                </SignedOut>
                <SignedIn>
                  <UserButton />
                </SignedIn>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Hero Carousel - Featured Articles (no title) */}
            <section>
              <Carousel className="w-full" setApi={setHeroApi} onMouseEnter={() => setHeroHover(true)} onMouseLeave={() => setHeroHover(false)}>
                <CarouselContent>
                  {featuredArticles.map((article) => (
                    <CarouselItem key={article.id} className="flex min-h-0">
                      <Card className="group overflow-hidden border-slate-200 hover:shadow-2xl transition-all duration-500 bg-white cursor-pointer h-72 md:h-96 flex flex-col min-h-0">
                        <div className="grid md:grid-cols-2 gap-0 h-full items-stretch" onClick={() => navigate(`/article/${article.id}`)}>
                          <div className="relative overflow-hidden h-full min-h-0 flex-shrink-0">
                            <img 
                              src={getImageUrl(article.imageUrl)} 
                              alt={article.title}
                              className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                            <Badge className={`absolute top-4 left-4 text-white border-0 px-4 py-1.5 ${categoryColorMap[article.category] || categoryColorMap['default']}`}>
                              {article.category}
                            </Badge>
                          </div>
                          <CardContent className="p-8 flex flex-col justify-center h-full">
                            <h3 className="font-bold text-3xl mb-4 text-slate-800 group-hover:text-indigo-600 transition-colors">
                              {article.title}
                            </h3>
                            <p className="text-slate-600 text-base mb-6 leading-relaxed">{article.excerpt}</p>
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <p className="text-sm font-medium text-slate-700">{article.source}</p>
                                <p className="text-xs text-slate-500">{new Date(article.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                              </div>
                              <Button 
                                className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg hover:shadow-xl transition-all"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/article/${article.id}`);
                                }}
                              >
                                Read Summary <ExternalLink className="w-4 h-4 ml-2" />
                              </Button>
                            </div>
                          </CardContent>
                        </div>
                      </Card>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {/* Dots / pagination indicators for hero (overlay inside carousel, subtle and smaller) */}
                <div className="absolute left-1/2 -translate-x-1/2 bottom-6 z-40 flex items-center gap-2">
                  {featuredArticles.map((_, idx) => (
                    <button
                      key={`dot-${idx}`}
                      onClick={() => { if (heroApi) heroApi.scrollTo(idx); setHeroSelected(idx); }}
                      aria-label={`Go to slide ${idx + 1}`}
                      className={`rounded-full transition-all ${heroSelected === idx ? 'bg-slate-700 w-2 h-2' : 'bg-slate-400 w-1.5 h-1.5 hover:bg-slate-500'}`}>
                    </button>
                  ))}
                </div>
              </Carousel>
            </section>

            {/* Ad banner below hero */}
            {!isPremium && (
              <section className="w-full">
                <Card className="border-slate-200 shadow-lg bg-white overflow-hidden">
                  <CardContent className="p-0">
                    <img
                      src="/cocacola.gif"
                      alt="Advertisement"
                      className="w-full h-auto object-contain"
                    />
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Category Filter with horizontal scroll + Refresh Button */}
            <section
              ref={categoryRef}
              className="sticky z-40 bg-white/90 backdrop-blur-lg rounded-xl p-4 shadow-md border border-slate-200 overflow-hidden"
              style={ categoryTop ? { top: `${categoryTop}px` } : undefined }
            >
              <div className="flex items-center gap-3">
                {/* Scrollable tabs container */}
                <div className="flex-1">
                  <div ref={scrollContainerRef} className="overflow-x-auto no-scrollbar py-1 pr-3 pl-4">
                    <div ref={categoryScrollRef} className="flex gap-2 items-center whitespace-nowrap">
                      {categories.map((category, idx) => {
                        const IconComponent = iconMap[category.icon];
                        return (
                          <React.Fragment key={category.id}>
                            <Button
                              id={`cat-btn-${category.id}`}
                              variant={selectedCategory === category.id ? "default" : "outline"}
                              size="sm"
                              onClick={() => selectAndScroll(category.id)}
                              className={`flex-shrink-0 transition-all duration-300 ${
                                selectedCategory === category.id 
                                  ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md scale-105' 
                                  : 'text-slate-700 hover:bg-slate-100 border-slate-300 hover:scale-105'
                              }`}
                            >
                              <IconComponent className="w-4 h-4 mr-2" />
                              {category.name}
                            </Button>
                            {/* subtle vertical separator between tabs (except after last) */}
                            {idx < categories.length - 1 && (
                              <div className="h-5 w-px bg-slate-200 mx-1" aria-hidden="true" />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Refresh button stays fixed on the right */}
                <div className="flex-shrink-0 ml-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="text-slate-700 hover:bg-indigo-50 border-slate-300 hover:border-indigo-600 transition-all"
                  >
                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {/* Left / right subtle fades to indicate more content (appear when needed) */}
              {showLeftFade && (
                <div className="absolute left-4 top-0 bottom-0 w-8 pointer-events-none bg-gradient-to-r from-white/90 to-transparent" />
              )}
              {showRightFade && (
                <div className="absolute right-16 top-0 bottom-0 w-8 pointer-events-none bg-gradient-to-l from-white/90 to-transparent" />
              )}
            </section>

            {/* Videos Section with Carousel */}
            {filteredVideos.length > 0 && (
              <section className="space-y-4">
                <div className="flex items-center gap-2">
                  <Youtube className="w-5 h-5 text-red-600" />
                  <h2 className="text-2xl font-bold text-slate-800">Recent Videos</h2>
                </div>
                <Carousel className="w-full">
                  <CarouselContent>
                    {filteredVideos.map((video) => (
                      <CarouselItem key={video.id} className="md:basis-1/2 lg:basis-1/2">
                        <Card 
                          className="group overflow-hidden border-slate-200 hover:shadow-2xl transition-all duration-300 bg-white cursor-pointer"
                          onClick={() => window.open(video.videoUrl || `https://www.youtube.com/watch?v=${video.videoId}`, '_blank')}
                        >
                          <div className="relative overflow-hidden h-48">
                            <img 
                              src={getImageUrl(video.thumbnail)} 
                              alt={video.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                              <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center transform group-hover:scale-125 transition-transform shadow-2xl">
                                <Youtube className="w-8 h-8 text-white" />
                              </div>
                            </div>
                            <Badge className={getBadgeClass(video.category)}>
                              {video.category}
                            </Badge>
                          </div>
                          <CardContent className="p-4">
                            <h3 className="font-semibold text-lg mb-2 text-slate-800 line-clamp-2 group-hover:text-red-600 transition-colors">
                              {video.title}
                            </h3>
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-slate-500">{video.channel}</p>
                              <ExternalLink className="w-3 h-3 text-slate-400" />
                            </div>
                          </CardContent>
                        </Card>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="-left-12" />
                  <CarouselNext className="-right-12" />
                </Carousel>
              </section>
            )}

              {/* Inline ad banner between videos and articles */}
              {!isPremium && (
                <section className="w-full">
                  <Card className="border-transparent shadow-lg overflow-hidden">
                    <CardContent className="p-0">
                      <div className="rounded-lg p-[1px] bg-gradient-to-r from-blue-600 to-indigo-600">
                        <div className="min-h-28 w-full bg-[#0f172a] relative rounded-lg p-5 text-center">
                          {/* Blue Radial Glow Background */}
                          <div
                            className="absolute inset-0 z-0"
                            style={{
                              backgroundImage: `radial-gradient(circle 600px at 50% 50%, rgba(59,130,246,0.3), transparent)`,
                            }}
                          />
                          <div className="relative z-10 text-white">
                            <Coffee className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                            <p className="text-sm mb-3">If you want to enjoy all content without ads, subscribe to Premium — €1/month. Cheaper than a coffee!</p>
                            <a href="/suscription" className="inline-block mt-1 px-4 py-2 bg-amber-400 hover:bg-amber-500 rounded-md text-slate-900 font-semibold">Subscribe →</a>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </section>
              )}

            {/* Articles Grid */}
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-indigo-600" />
                <h2 className="text-2xl font-bold text-slate-800">Latest News</h2>
                <span className="text-sm text-slate-500 ml-2">({filteredArticles.length} results)</span>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {paginatedArticles.map((article) => (
                  <Card 
                    key={article.id} 
                    className="group overflow-hidden border-slate-200 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white cursor-pointer"
                    onClick={() => navigate(`/article/${article.id}`)}
                  >
                    <div className="relative overflow-hidden h-48">
                      <img 
                        src={getImageUrl(article.imageUrl)} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      <Badge className={getBadgeClass(article.category)}>
                        {article.category}
                      </Badge>
                    </div>
                    <CardContent className="p-5">
                      <h3 className="font-bold text-lg mb-2 text-slate-800 group-hover:text-indigo-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-slate-600 text-sm mb-3 line-clamp-2">{article.excerpt}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-600 font-medium flex items-center gap-1">
                          {article.source}
                          <ExternalLink className="w-3 h-3" />
                        </span>
                        <span className="text-slate-400">{new Date(article.publishedAt).toLocaleDateString('en-US')}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-1 mt-8 overflow-x-auto py-2 no-scrollbar">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="text-slate-700"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  {generatePaginationNumbers(currentPage, totalPages).map((pageNum, index) => (
                    pageNum === '...' ? (
                      <span key={`ellipsis-${index}`} className="px-2 py-1 text-slate-500">
                        ...
                      </span>
                    ) : (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className={currentPage === pageNum ? 'bg-indigo-600 text-white' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'}
                      >
                        {pageNum}
                      </Button>
                    )
                  ))}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="text-slate-700"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Newsletter */}
            <Card className="border-slate-200 shadow-lg bg-gradient-to-br from-indigo-600 via-indigo-700 to-blue-700 text-white overflow-hidden sticky top-24">
              <CardContent className="p-6 relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
                <Mail className="w-10 h-10 mb-4 relative z-10" />
                <h3 className="font-bold text-xl mb-2 relative z-10">Newsletter</h3>
                <p className="text-sm mb-4 text-indigo-100 relative z-10">
                  Receive the latest AI news directly in your inbox
                </p>
                {/* Mailchimp embedded form (submits to Mailchimp list) */}
                <form
                  action="https://coder-labs.us6.list-manage.com/subscribe/post?u=e1d4e0c6fb91df9250dcc9ebb&amp;id=f35dcb5aa9&amp;f_id=00d8c2e1f0"
                  method="post"
                  id="mc-embedded-subscribe-form"
                  name="mc-embedded-subscribe-form"
                  className="space-y-3 relative z-10"
                  target="_blank"
                  noValidate
                >
                  <label htmlFor="mce-EMAIL" className="sr-only">Email address</label>
                  <Input
                    type="email"
                    name="EMAIL"
                    id="mce-EMAIL"
                    placeholder="you@example.com"
                    required
                    className="bg-white/20 border-white/30 text-white placeholder:text-indigo-200 focus:bg-white/30 transition-colors"
                  />

                  {/* responses */}
                  <div id="mce-responses" className="clear foot">
                    <div className="response" id="mce-error-response" style={{display: 'none'}}></div>
                    <div className="response" id="mce-success-response" style={{display: 'none'}}></div>
                  </div>

                  {/* hidden anti-bot field expected by Mailchimp */}
                  <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                    <input type="text" name="b_e1d4e0c6fb91df9250dcc9ebb_f35dcb5aa9" tabIndex="-1" defaultValue="" />
                  </div>

                  <div className="optionalParent">
                    <div className="clear foot">
                      <Button type="submit" className="w-full bg-white text-indigo-700 hover:bg-indigo-50 font-semibold transition-all hover:scale-105 shadow-lg">
                        Subscribe
                      </Button>
                      <p className="mt-2 text-xs text-indigo-100">
                        Al suscribirte aceptarás recibir correos con novedades. Puedes cerrar esta ventana si lo deseas.
                      </p>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Ad Space */}
            {!isPremium && (
              <Card className="border-slate-200 shadow-lg bg-white overflow-hidden">
                <CardContent className="p-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_aidevnewshub/artifacts/nnlcavaq_Banner-Ad.gif" 
                    alt="Advertisement" 
                    className="w-full h-auto object-contain"
                  />
                </CardContent>
              </Card>
            )}

            {/* Filter by Year and Month */}
            <Card className="border-slate-200 shadow-lg bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-bold text-lg text-slate-800">Filter by Date</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-slate-600 mb-1 block">Year</label>
                    <Select value={selectedYear} onValueChange={setSelectedYear}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All years</SelectItem>
                        {availableYears.map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 mb-1 block">Month</label>
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All months</SelectItem>
                        {availableMonths.map(month => (
                          <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(selectedYear !== 'all' || selectedMonth !== 'all') && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        setSelectedYear('all');
                        setSelectedMonth('all');
                      }}
                      className="w-full text-slate-600 hover:text-indigo-600"
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
                <h4 className="font-bold text-lg mb-3">AI News Hub</h4>
              <p className="text-slate-400 text-sm">
                Your trusted source for AI, programming and technology news.
              </p>
            </div>
            {/* YouTube channels removed per request */}
            <div>
              <h4 className="font-bold text-lg mb-3">Resources</h4>
              <div className="space-y-2 text-sm text-slate-400">
                <a href="https://intheworldofai.com/" target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors">In The World of AI</a>
                <a href="https://www.hubdev.tools/category/ai" target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors">HubDev — AI</a>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-3">Categories</h4>
              <div className="space-y-2 text-sm text-slate-400">
                <p>AI News</p>
                <p>Programming</p>
                <p>Machine Learning</p>
                <p>Research Papers</p>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-400 text-sm">
              © 2024 AI News Hub. Automated collection of AI and programming news.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;